//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import {
  IDeleteAccountRequest,
  IDeleteAccountResponse,
  IDeletedAccountId,
  IGetAvailableInstagramAccountsResponse,
  IGetConnectedInstagramAccountResponse,
  IGetConnectedInstagramAccountsResponse,
  ISetConnectedInstagramAccountRequest,
  ISetV2ConnectedInstagramAccountRequest,
} from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import InstagramUserId from '@models/InstagramUserId';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function gatewayInstagramSettingsFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/settings/instagram${endpoint}`,
    init,
  );
}

// Multi Handle support
function gatewayInstagramSettingsV2Fetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v2/settings/instagram${endpoint}`,
    init,
  );
}

/**
 * Get all Instagram accounts associated with the currently connected user
 *
 * /api/v1/settings/instagram/available-accounts
 */
export const getAvailableInstagramAccounts = async (
  init?: RequestInit,
): Promise<IGetAvailableInstagramAccountsResponse> => {
  const res = await gatewayInstagramSettingsFetch(`/available-accounts?`, {
    ...init,
  });
  return res.json<IGetAvailableInstagramAccountsResponse>();
};

/**
 *
 * Gets the Instagram business account currently connected to this client
 *
 * /api/v1/settings/instagram/connected-account
 *
 */
export const getConnectedInstagramAccount = async (
  init?: RequestInit,
): Promise<IGetConnectedInstagramAccountResponse> => {
  const res = await gatewayInstagramSettingsFetch('/connected-account', {
    ...init,
  });
  return res.json<IGetConnectedInstagramAccountResponse>();
};

/**
 * Set the connected Instagram account for the current client
 *
 * /api/v1/settings/instagram/connected-account
 */
export const setConnectedInstagramAccount = async (
  request: ISetConnectedInstagramAccountRequest,
  init?: RequestInit,
): Promise<void> => {
  await gatewayInstagramSettingsFetch(`/connected-account`, {
    body: JSON.stringify(request),
    method: 'POST',
    ...init,
  });
};

/** Multi Handle support
 * get the connected Instagram accounts for the current client
 *
 * /api/v2/settings/instagram/connected-accounts
 */
export const getConnectedMultiHandleInstagramAccount = async (
  init?: RequestInit,
): Promise<IGetConnectedInstagramAccountsResponse> => {
  const res = await gatewayInstagramSettingsV2Fetch('/connected-accounts', {
    ...init,
  });
  const result = await res.json<IGetConnectedInstagramAccountsResponse>();
  return result;
};

/**
 * Set the connected Instagram account for the current client
 *
 * /api/v2/settings/instagram/connected-accounts
 */
export const setConnectedMultiHandleInstagramAccount = async (
  request: ISetV2ConnectedInstagramAccountRequest,
  init?: RequestInit,
): Promise<void> => {
  await gatewayInstagramSettingsV2Fetch(`/connected-accounts`, {
    body: JSON.stringify(request),
    method: 'POST',
    ...init,
  });
};

/**
 * Set the connected Instagram account as Default
 *
 * /api/v2/settings/instagram/connected-accounts/default
 */
export const setConnectedInstagramAccountDefault = async (
  request: ISetConnectedInstagramAccountRequest,
  init?: RequestInit,
): Promise<void> => {
  await gatewayInstagramSettingsV2Fetch(`/connected-accounts/default`, {
    body: JSON.stringify(request),
    method: 'PUT',
    ...init,
  });
};

/**
 * Delete comment by id
 *
 * DELETE * /api/v2/settings/instagram/connected-accounts/:instagramUserId
 */
export const deleteInstgramMultiHandleAccount = async (
  req: IDeleteAccountRequest,
  init?: RequestInit,
): Promise<void> => {
  const { instagramUserId } = req;
  await gatewayInstagramSettingsV2Fetch(
    `/connected-accounts/${instagramUserId}`,
    {
      method: 'DELETE',
      ...init,
    },
  );
};
